import classnames from 'classnames'
import * as React from 'react'
import { connect } from 'react-redux'

import { filterChanged } from 'common/actions'
import { lifeInsuranceOfferSelectedSelector } from 'embed/selectors/rates'
import { brandSelector } from 'embed/selectors/config'
import { formattedPhoneNumber, getBrandObject } from 'embed/utils'
import { Brand, Filter, Product } from 'even-ts-static'

import { MultiProductDispatch } from 'embed/constants'
import { MultiProductState } from 'embed/reducers'

const styles = require('embed/styles/application')

interface Props {
  brand: Brand
  children?: React.ReactNode
  className?: string
  dispatchTCPALanguageChanged: (language: string) => void
  offerPartnerCompanyUuid: string | undefined
}

const getLanguage = (brand: Brand, offerPartnerCompanyUuid?: string) => {
  const contact = getBrandObject(brand).contact
  const name = contact.Name
  const phone = contact.Phone
  const url = contact.Url

  let language

  switch (offerPartnerCompanyUuid) {
    // We're hard-coding custom language for Legal and General in dev and prod. If we end up having to do this for a
    // number of partners, then we'll have to implement a programmatic solution.
    case '198f5e46-b5e8-454f-81bd-1741bcf97637':
    case '976c4fe7-b2cb-4878-b6d7-d5ff8d81f5c2':
      language = {
        html: (
          <span>
            By clicking agree and continue I hereby consent to the{' '}
            <a href={`${url}/terms-of-service/`} target="_blank">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href={`${url}/privacy-policy/`} target="_blank">
              Privacy Policy
            </a>
            . I consent to be contacted and receive calls, emails, and text messages from LeapLife, Legal & General
            America, and their partners, financial professionals, third parties calling on their behalf and their
            affiliated companies, regarding Legal & General America products and services as well as to explore various
            financial products and services at the phone number(s) I provided above, including my wireless number if
            provided. I understand these calls may be generated using an automatic telephone dialing system or
            prerecorded voice message for telemarketing purposes. I understand to make a purchase I do not need to
            consent to receive calls and can instead contact a customer care representative by calling (800) 614-7505.
          </span>
        ),
        text: `By clicking agree and continue I hereby consent to the Terms of Service and Privacy Policy. I consent
          to be contacted and receive calls, emails, and text messages from LeapLife, Legal & General America, and
          their partners, financial professionals, third parties calling on their behalf and their affiliated companies,
          regarding Legal & General America products and services as well as to explore various financial products and
          services at the phone number(s) I provided above, including my wireless number if provided. I understand
          these calls may be generated using an automatic telephone dialing system or prerecorded voice message for
          telemarketing purposes. I understand to make a purchase I do not need to consent to receive calls and can
          instead contact a customer care representative by calling (800) 614-7505.`,
      }
      break
    default:
      language = {
        html: (
          <span>
            By clicking agree and continue I hereby consent to the{' '}
            <a href={`${url}/terms-of-service/`} target="_blank">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href={`${url}/privacy-policy/`} target="_blank">
              Privacy Policy
            </a>
            . I agree to be contacted by {name} and its partners and their affiliated companies via email and/or at the
            telephone number(s) I have provided, including contact through automatic dialing systems, artificial or
            pre-recorded voice messaging, or text message. Consent is not required as a condition to utilize {name} and
            you may choose to be contacted by an individual customer care representative(s) by calling{' '}
            {formattedPhoneNumber(phone)}.
          </span>
        ),
        text: `By clicking agree and continue I hereby consent to the Terms of Service and Privacy Policy.
        I agree to be contacted by ${name} and its partners and their affiliated companies
        via email and/or at the telephone number(s) I have provided, including contact
        through automatic dialing systems, artificial or pre-recorded voice messaging,
        or text message. Consent is not required as a condition to utilize ${name} and
        you may choose to be contacted by an individual customer care representative(s)
        by calling ${formattedPhoneNumber(phone)}.`,
      }
  }

  return language
}

const TCPALanguage: React.FC<Props> = ({
  brand,
  children,
  className,
  dispatchTCPALanguageChanged,
  offerPartnerCompanyUuid,
}) => {
  const [language] = React.useState(getLanguage(brand, offerPartnerCompanyUuid))

  React.useEffect(() => {
    dispatchTCPALanguageChanged(language.text)
  }, [])

  return (
    <div className={classnames(styles.formRow, styles.consentField, className)}>
      <label>{language.html}</label>
      {children}
    </div>
  )
}

const mapStateToProps = (state: MultiProductState) => ({
  brand: brandSelector(state),
  offerPartnerCompanyUuid: lifeInsuranceOfferSelectedSelector(state)?.partner?.uuid,
})

const mapDispatchToProps = (dispatch: MultiProductDispatch) => ({
  dispatchTCPALanguageChanged: (language: string) =>
    dispatch(filterChanged(Product.LifeInsurance, Filter.TCPALanguage, language)),
})

export const AcceptAndContinueLanguage = connect(mapStateToProps, mapDispatchToProps)(TCPALanguage)
